import { UserClient } from 'api/clients/UserClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ArticleService } from 'api/services/ArticleService'
import { useGlobalEvent } from 'beautiful-react-hooks'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useGuestWelcomeModal } from 'components/GuestWelcomeModal/hooks/useGuestWelcomeModal'
import { useHandbookBreadcrumb } from 'components/Handbook2_0/hooks'
import NotFound from 'components/NotFound/NotFound'
import PasswordRequest from 'components/PasswordRequest/PasswordRequest'
import RequestAccess from 'components/RequestAccess/RequestAccess'
import { articlesKeys } from 'hooks/queries/articles/articlesKeys'
import { useFolder } from 'hooks/queries/folders/useFolder'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useArticlePassword } from 'hooks/useArticlePassword'
import { useArticleSubscription } from 'hooks/useArticleSubscription'
import { useCommandLine } from 'hooks/useCommandLine'
import { useCurrentLayer } from 'hooks/useCurrentLayer'
import { useFavIconEmoji } from 'hooks/useFavIconEmoji'
import { useIframeWrapperSetup } from 'hooks/useIframeWrapper'
import { useInaccessibleLayerAlert } from 'hooks/useInaccessibleLayerAlert'
import { useSlugSentry } from 'hooks/useSlugSentry'
import { useSuggestions } from 'hooks/useSuggestions'
import { useUnregisteredUser } from 'hooks/useUnregisteredUser'
import { useWorkspace } from 'hooks/useWorkspace'
import isEqual from 'lodash/isEqual'
import { observer } from 'mobx-react-lite'
import { LOGIN_OR_SIGNUP_PATH } from 'paths'
import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { useThirdPartyAnycableConsumer } from 'providers/CableConsumer/useDebugConsumer'
import { useDebugWebSocket } from 'providers/DebugWebSocket'
import qs from 'qs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Helmet from 'react-helmet'
import { useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import * as Actions from 'redux/actions'
import { articleMetaSet, breadcrumbsContextSet } from 'redux/actions'
import { setAnonymousUser } from 'redux/actions/anonymousUser'
import { setCurrentArticleId } from 'redux/actions/currentArticleId'
import store from 'redux/store'
import { renderPrintPreview } from 'screens/PrintPreview/PrintPreview'
import ProseEdit from 'screens/ProseEdit/ProseEdit'
import { useBrand } from 'hooks/useBrand'
import ProseRead from 'screens/ProseEdit/ProseRead'
import { ReviewsBySection } from 'screens/ProseEdit/components/ReviewsBySection/ReviewsBySection'
import { AuthStore, AuthStoreContext } from 'stores/AuthStore'
import { DocStoreContext } from 'stores/DocStore'
import { useRootStore } from 'stores/RootStore'
import { ArticleFlat, ArticleView, BreadcrumbsContextType, Layer, LayerSummary, Permission, RootState } from 'types'
import { currentlyInCore } from 'utils/articleUtils'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { navigateToExternalUrl } from 'utils/location'
import { isHelpCenterWorkspace } from 'utils/workspaceUtils'
import { ConditionalWrapper } from '../../common/ConditionalWrapper/ConditionalWrapper'
import { useAlmanacFile } from '../../hooks/queries/almanacFiles/useAlmanacFile'
import { AssetType, useAlmanacFileSubscription } from '../../hooks/useAlmanacFileSubscription'
import { useQueryParams } from '../../hooks/useQueryParams'
import { DocMode } from '../../stores/DocStore.types'
import { ArticleViewRedirect } from './ArticleViewRedirect'
import TrackedChangesStoreSync from './components/TrackedChangesStoreSync/TrackedChangesStoreSync'
import { WorkflowProviderContainer } from './components/WorkflowProvider/WorkflowProviderContainer'
import { useDocStoreSync } from './useDocStoreSync'
import * as Layout from 'components/Layout'
import { ArticlePostPreviewProvider } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { EmailBlockWrapperV2 } from 'components/EmailBlock/EmailBlockWrapperV2'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { EmailChannel } from 'components/EmailBlock/constants'
interface Props {
  id: string
  className?: string
}

export enum AuthorizationState {
  Authorized = 'authorized',
  PasswordNeeded = 'password_needed',
  RequestAccess = 'request_access',
}

export const highestUserAccess = (layer: Layer | undefined) => {
  return layer?.permissions?.highest || Permission.Read
}

export const ArticleViewWrapper = observer(({ id, className }: Props) => {
  const rootStore = useRootStore()
  const queryClient = useQueryClient()
  const history = useHistory()
  const location = useLocation<{ cleanupMode?: boolean }>()
  const dispatch = useDispatch()
  const [notFound, setNotFound] = useState<Record<string, boolean>>({})
  const [openDoc, setOpenDoc] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const { favicon } = useBrand()
  const { isChannelPostable } = useIsChannelPostable()
  const [loadingAnonymousUser, setLoadingAnonymousUser] = useState(true)
  const mailchimpEnabled = useFlipperFlag('mailchimp_integration')
  const { auth, articleMeta } = useSelector(
    (state: RootState) => ({
      auth: state.auth,
      articleMeta: state.articleMeta[id],
    }),
    isEqual,
  )
  useAlmanacFile(id, {
    onSuccess: (data) => {
      rootStore.files.updateAttrs(id, data.almanac_file)
      dispatch(
        breadcrumbsContextSet({
          type: BreadcrumbsContextType.AlmanacFile,
          id: id,
          ancestors: data.almanac_file.ancestors,
          assetType: 'Article',
        }),
      )
    },
  })

  const articlePassword = useArticlePassword(id)
  const articleView = useArticleView()
  const layerView = (layer: LayerSummary) => (layer.is_main ? articleView || ArticleView.Reading : ArticleView.Editing)

  /* Preload layers */

  const [loggedIn, setLoggedIn] = useState(!!auth)
  const [article, setArticle] = useState<ArticleFlat>()
  const [authorizationState, setAuthorizationState] = useState<AuthorizationState>(AuthorizationState.Authorized)
  const workspaceId = auth && auth.company_id
  const currentWorkspace = useWorkspace(workspaceId)
  const unregisteredUser = useUnregisteredUser()
  const layerId = useQueryParams().layer || article?.main_layer.id
  const layer = useCurrentLayer({ articleKey: id })
  const userHighestAccess = highestUserAccess(layer)
  const { setCurrentFolder } = useCurrentFolderStore()

  const renderGuestWelcomeModal = useGuestWelcomeModal(
    userHighestAccess,
    currentWorkspace,
    article?.guest_inviter_display_name,
  )

  const scheduledPostId = article?.almanac_file?.scheduled_post_id

  const params = useMemo(() => {
    return qs.parse(location.search, { ignoreQueryPrefix: true })
  }, [location.search])

  const unauthConfirmToken = !auth && params.unauthConfirmToken
  const invitedUser = !!params.inviter
  const { handbook } = useHandbookBreadcrumb()
  const docStore = useDocStoreSync(id)
  const [authStore] = useState(() => new AuthStore(rootStore, auth))

  useDebugWebSocket()
  useThirdPartyAnycableConsumer()
  // TODO: Not the best place to hook it but this FF should be short lived
  useIframeWrapperSetup()

  useEffect(() => {
    return () => {
      authStore.destroy()
    }
  }, [authStore])

  useEffect(() => {
    if (layer && docStore) {
      docStore.setLayerId(layer.id)
    }
  }, [docStore, layer])

  useEffect(() => {
    docStore?.setArticleId(id)
  }, [docStore, id])

  // If the user inputs a wrong article slug, redirect to the right slug
  useSlugSentry(article)

  useEffect(() => {
    dispatch(setCurrentArticleId(id))

    return () => {
      dispatch(setCurrentArticleId(null))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (auth && !loggedIn) {
      setLoggedIn(true)
    }
    if (!auth && loggedIn) {
      setLoggedIn(false)
    }
  }, [auth, loggedIn])

  useEffect(() => {
    if (loggedIn || !openDoc || !!unregisteredUser.user) {
      setLoadingAnonymousUser(false)
      return
    }
    UserClient.authenticateAnonymousUser().then((response) => {
      if (response.type === SUCCESS_RESPONSE_TYPE) {
        dispatch(setAnonymousUser(response.data.anonymous_user))
        dispatch(Actions.headersSet({ headers: response.headers }))
        authStore.startRefresh()
      } else {
        showToastMessage("Couldn't authenticate. Please try again later.", 'error')
      }
      setLoadingAnonymousUser(false)
    })
  }, [loggedIn, authStore, openDoc, dispatch, unregisteredUser.user])

  useArticleSubscription({ id })
  useAlmanacFileSubscription({ asset: { id, type: AssetType.Article } })

  const articleLoaded = article && article.id === id

  const fetchArticle = useCallback(
    async (withoutSpinner?: boolean) => {
      if (loadingAnonymousUser || articleLoaded || unregisteredUser.loading) return

      if (!withoutSpinner) setArticle(undefined)

      const res = await ArticleService.getArticle(id, {
        withAccessMap: true,
        enteredPassword: articlePassword,
        handbookKey: handbook?.key,
        withAncestors: true,
        withArticleView: true,
      })
      if (res.type === 'success') {
        const {
          data: { article },
        } = res

        // warm the react query cache
        queryClient.setQueryData<{ article: ArticleFlat }>(
          articlesKeys.detail(article.id, { password: articlePassword }),
          () => ({ article }),
        )

        const { company_id, shared_to_web, made_public } = article
        setArticle(article)
        dispatch(articleMetaSet(article))

        const hasWorkspaceAccess = store.getState().workspace.workspaces.some((w) => w.id === company_id)

        if (!loggedIn && shared_to_web) setOpenDoc(true)

        if (hasWorkspaceAccess) {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          dispatch(Actions.currentWorkspaceIdSet(company_id!))
        } else if (made_public) {
          dispatch(Actions.currentWorkspaceIdSet(ALMANAC_CORE_WORKSPACE_ID))
        }

        if (article.show_in_read_mode) {
          const urlWithParams = location.search
            ? `${location.pathname}${location.search}&request_read=true`
            : `${location.pathname}?request_read=true`
          history.push(urlWithParams)
        }

        if (article.locked && article.master_branch && !!unauthConfirmToken) {
          history.replace(`${article.master_branch.url}${location.search}`)
        }

        setAuthorizationState(AuthorizationState.Authorized)
        setLoading(false)
      } else if (res.type === 'error') {
        if (res.status === 401) {
          setAuthorizationState(AuthorizationState.PasswordNeeded)
        } else if (res.status === 403) {
          setAuthorizationState(AuthorizationState.RequestAccess)
        } else if (res.status === 301) {
          const {
            data: {
              article: { blog_redirect_path },
            },
          } = res

          if (!!blog_redirect_path) {
            navigateToExternalUrl(`https://almanac.io/blog/${blog_redirect_path}`, { replaceHistory: true })
          }
        }
        if (res.status === 404) {
          setNotFound({ ...notFound, [id]: true })
        }
        setLoading(false)
      }
    },

    // Ignore "updateArticleMeta" dependency, don't refetch an article on the function change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, loadingAnonymousUser, articlePassword, unregisteredUser.loading, articleMeta?.cover_image_url],
  )

  useEffect(() => {
    fetchArticle()
  }, [fetchArticle])

  useEffect(() => {
    if (auth) return

    if (invitedUser)
      history.push(`${LOGIN_OR_SIGNUP_PATH}?redirect_to=${location.pathname}&${location.search.substring(1)}`)
  }, [auth, history, invitedUser, location.search, location.pathname])

  useSuggestions(auth?.id, id, articleLoaded)

  const overridePrint = async (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
      event.preventDefault()
      renderPrintPreview({ articleKey: id })
    }
  }

  useGlobalEvent('keydown', undefined, overridePrint)

  useFavIconEmoji(article && articleMeta?.header_emoji, favicon)

  // Warm the cache to make event timeline feel faster
  const userId = auth?.id || params?.userId
  const inCore = currentlyInCore(article)
  const inHelpCenter = currentWorkspace && isHelpCenterWorkspace(currentWorkspace.id)
  useEffect(() => {
    if (!inCore) {
      ArticleService.fetchTimelineEvents(id, userId?.toString?.(), false)
    }
  }, [inCore, id, userId])

  const { addContext, removeContext } = useCommandLine()
  useEffect(() => {
    if (!article) return

    addContext('article', article)

    return () => removeContext('article')
  }, [addContext, article, removeContext])

  useEffect(() => {
    if (!layer) return

    addContext('layer', layer)

    return () => removeContext('layer')
  }, [addContext, layer, removeContext])

  useInaccessibleLayerAlert({ articleKey: id, currentLayerId: useQueryParams().layer })

  useEffect(() => {
    if (docStore && location.state?.cleanupMode && !docStore.inCleanupMode) {
      docStore.setDocMode(DocMode.Cleanup)
    }
  }, [docStore, location.state])

  const { data: folderData } = useFolder(articleMeta?.project_key)
  useEffect(() => {
    if (folderData?.folder) setCurrentFolder(folderData?.folder)
  }, [folderData?.folder, setCurrentFolder])

  const searchParams = new URLSearchParams(location.search)
  if (auth && searchParams.get('email')) {
    searchParams.delete('email')
    history.push(`${location.pathname}?${searchParams}`)
    return null
  }

  if (notFound[id]) return <NotFound />

  if (authorizationState === AuthorizationState.PasswordNeeded) return <PasswordRequest fileId={id} />
  if (authorizationState === AuthorizationState.RequestAccess)
    return <RequestAccess type="doc" workspaceId={workspaceId} articleId={id} />

  return (
    <>
      <Helmet>
        <script>{`window.prerenderReady = ${!loading}; `}</script>
      </Helmet>

      {renderGuestWelcomeModal()}
      {article && layer && (
        <ConditionalWrapper
          condition={
            auth &&
            !inCore &&
            !Boolean(inHelpCenter) &&
            Boolean(articleMeta?.workflow_property_value) &&
            Boolean(articleMeta?.project_key)
          }
          wrapper={(children) => (
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <WorkflowProviderContainer articleId={id} folderId={articleMeta?.project_key!}>
              {children}
            </WorkflowProviderContainer>
          )}
        >
          <ArticleViewRedirect article={article} layer={layer}>
            <div className={className}>
              <AuthStoreContext.Provider value={authStore}>
                <DocStoreContext.Provider value={docStore}>
                  <ArticlePostPreviewProvider fileId={article.almanac_file.id}>
                    <ReviewsBySection />
                    <TrackedChangesStoreSync />
                    {mailchimpEnabled && workspaceId && scheduledPostId && (
                      <EmailBlockWrapperV2
                        workspaceId={workspaceId}
                        scheduledPostId={scheduledPostId}
                        channel={article.almanac_file.channel as EmailChannel}
                      />
                    )}
                    {isChannelPostable(article.channel) && <Layout.PostPreviewContainer />}
                    {[ArticleView.Commenting, ArticleView.Editing].includes(layerView(layer)) ? (
                      <ProseEdit
                        key={layerView(layer) === ArticleView.Commenting ? 'commenting' : 'editing'}
                        article={article}
                        layerId={layerId}
                        canEdit={layer?.permissions.can_edit}
                        withCommentViewOnly={layerView(layer) === ArticleView.Commenting}
                      />
                    ) : (
                      <ProseRead layerId={layerId} article={article} />
                    )}
                  </ArticlePostPreviewProvider>
                </DocStoreContext.Provider>
              </AuthStoreContext.Provider>
            </div>
          </ArticleViewRedirect>
        </ConditionalWrapper>
      )}
    </>
  )
})

export default ArticleViewWrapper
